

interface VideoElement {
  video: HTMLVideoElement;
  peerId?: string;
  isLocal: boolean;
  kind?: 'camera' | 'screen';
}

class RecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private isRecording: boolean = false;
  private canvas: HTMLCanvasElement | null = null;
  private canvasCtx: CanvasRenderingContext2D | null = null;
  private animationFrame: number | null = null;
  private videoElements: VideoElement[] = [];
  private audioContext: AudioContext | null = null;
  private audioDestination: MediaStreamAudioDestinationNode | null = null;
  private audioSources: MediaStreamAudioSourceNode[] = [];
  private selectedVideoStream: MediaStream | null = null;
  private isScreenSharing: boolean = false;
  private localScreenStream: MediaStream | null = null;
  private remoteScreenShare: { peerId: string; stream: MediaStream } | null = null;

  constructor() {}

  /**
   * Starts recording the call
   * @param localStream - Local video/audio stream
   * @param remoteStreams - Map of peer ID to {audio, video} streams
   */
  async startRecording(
    localStream: MediaStream | null,
    remoteStreams: Map<string, { audio?: MediaStream; video?: MediaStream }>,
    options?: {
      selectedVideoStream?: MediaStream | null;
      isScreenSharing?: boolean;
      localScreenStream?: MediaStream | null;
      remoteScreenShare?: { peerId: string; stream: MediaStream } | null;
    }
  ): Promise<void> {
    if (this.isRecording) {
      console.warn('[Recording] Already recording');
      return;
    }

    this.recordedChunks = [];

    this.selectedVideoStream = options?.selectedVideoStream ?? null;
    this.isScreenSharing = options?.isScreenSharing ?? false;
    this.localScreenStream = options?.localScreenStream ?? null;
    this.remoteScreenShare = options?.remoteScreenShare ?? null;

    try {
      // Create canvas for video compositing (4K resolution for high quality recording)
      this.canvas = document.createElement('canvas');
      this.canvas.width = 1920;  // 1080p for recording (4K would be too heavy for encoding)
      this.canvas.height = 1080;
      this.canvasCtx = this.canvas.getContext('2d');

      // Set up audio mixing
      this.audioContext = new AudioContext();
      this.audioDestination = this.audioContext.createMediaStreamDestination();

      // Add local audio to the mix
      if (localStream) {
        const localAudioTracks = localStream.getAudioTracks();
        if (localAudioTracks.length > 0) {
          const localAudioStream = new MediaStream(localAudioTracks);
          const localSource = this.audioContext.createMediaStreamSource(localAudioStream);
          localSource.connect(this.audioDestination);
          this.audioSources.push(localSource);
        }
      }

      // Add remote audio streams to the mix
      remoteStreams.forEach((streams, _peerId) => {
        if (streams.audio && this.audioContext && this.audioDestination) {
          const remoteSource = this.audioContext.createMediaStreamSource(streams.audio);
          remoteSource.connect(this.audioDestination);
          this.audioSources.push(remoteSource);
        }
      });

      // Create video elements for compositing
      this.videoElements = [];

      // Add local video
      if (localStream && localStream.getVideoTracks().length > 0) {
        const localVideo = document.createElement('video');
        localVideo.srcObject = localStream;
        localVideo.muted = true;
        localVideo.autoplay = true;
        localVideo.playsInline = true;
        await localVideo.play();
        this.videoElements.push({ video: localVideo, isLocal: true, kind: 'camera' });
      }

      // Add remote videos (excluding the current screen share stream if present)
      remoteStreams.forEach((streams, peerId) => {
        if (streams.video) {
          const isScreenSharePeer = this.remoteScreenShare?.peerId === peerId;
          if (isScreenSharePeer) {
            return;
          }

          const remoteVideo = document.createElement('video');
          remoteVideo.srcObject = streams.video;
          remoteVideo.muted = true; // Audio handled separately
          remoteVideo.autoplay = true;
          remoteVideo.playsInline = true;
          remoteVideo.play().catch(console.error);
          this.videoElements.push({ video: remoteVideo, peerId, isLocal: false, kind: 'camera' });
        }
      });

      // Add screen share stream if active (local or remote)
      if (this.isScreenSharing && this.localScreenStream) {
        const screenVideo = document.createElement('video');
        screenVideo.srcObject = this.localScreenStream;
        screenVideo.muted = true;
        screenVideo.autoplay = true;
        screenVideo.playsInline = true;
        await screenVideo.play().catch(console.error);
        this.videoElements.push({ video: screenVideo, isLocal: true, kind: 'screen' });
      } else if (this.remoteScreenShare && this.remoteScreenShare.stream) {
        const screenVideo = document.createElement('video');
        screenVideo.srcObject = this.remoteScreenShare.stream;
        screenVideo.muted = true;
        screenVideo.autoplay = true;
        screenVideo.playsInline = true;
        screenVideo.play().catch(console.error);
        this.videoElements.push({ video: screenVideo, peerId: this.remoteScreenShare.peerId, isLocal: false, kind: 'screen' });
      }

      // Start drawing to canvas
      this._startCanvasDrawing();

      // Get canvas stream and add mixed audio
      const canvasStream = this.canvas.captureStream(30);
      const mixedAudioTrack = this.audioDestination.stream.getAudioTracks()[0];

      if (mixedAudioTrack) {
        canvasStream.addTrack(mixedAudioTrack);
      }

      // Create MediaRecorder
      const mimeType = this._getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond: 8000000, // 8 Mbps for high quality 1080p recording
      });

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this._stopCanvasDrawing();
        this._cleanup();
      };

      this.mediaRecorder.start(1000); // Collect data every second
      this.isRecording = true;

      console.log('[Recording] Started recording');
    } catch (error) {
      console.error('[Recording] Failed to start recording:', error);
      this._cleanup();
      throw error;
    }
  }

  /**
   * Stops recording and returns the recorded blob
   */
  async stopRecording(): Promise<Blob | null> {
    if (!this.isRecording || !this.mediaRecorder) {
      console.warn('[Recording] Not recording');
      return null;
    }

    return new Promise((resolve) => {
      if (!this.mediaRecorder) {
        resolve(null);
        return;
      }

      this.mediaRecorder.onstop = () => {
        this._stopCanvasDrawing();
        this._cleanup();

        const blob = new Blob(this.recordedChunks, {
          type: this._getSupportedMimeType(),
        });

        this.isRecording = false;
        this.recordedChunks = [];

        console.log('[Recording] Stopped recording, blob size:', blob.size);
        resolve(blob);
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Checks if the current browser is iOS Safari
   */
  private isIOSSafari(): boolean {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isIOS && isSafari;
  }

  /**
   * Downloads the recorded video
   * @param blob - The recorded video blob
   * @param filename - Optional filename
   * 
   *  no navigation (share api for IOS)
   */
  downloadRecording(blob: Blob, filename?: string): void {
    const extension = this._getExtensionFromMimeType(blob.type);
    const finalFilename = filename || `call-recording-${new Date().toISOString()}.${extension}`;

    if (this.isIOSSafari()) {
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], finalFilename, { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          navigator.share({
            files: [file],
            title: 'Call Recording',
          }).then(() => {
            console.log('[Recording] iOS Safari: Shared recording successfully');
          }).catch((err) => {
            console.warn('[Recording] iOS Safari: Share failed, using modal fallback', err);
            this._showInPageDownloadModal(blob, finalFilename);
          });
          return;
        }
      }
      // Fallback: Show in-page modal with video preview
      this._showInPageDownloadModal(blob, finalFilename);
    } else {
      // Desktop/Android: Use hidden iframe to trigger download without navigation
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        const a = iframeDoc.createElement('a');
        a.href = url;
        a.download = finalFilename;
        iframeDoc.body.appendChild(a);
        a.click();
      }
      
      // Cleanup after download starts
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);

      console.log('[Recording] Download initiated via iframe');
    }
  }
  // if share api fails 
  private _showInPageDownloadModal(blob: Blob, _filename: string): void {
    const url = URL.createObjectURL(blob);
    
    const modal = document.createElement('div');
    modal.id = 'recording-download-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      z-index: 99999;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `;
    
    modal.innerHTML = `
      <div style="color: white; text-align: center; max-width: 90%;">
        <h3 style="margin-bottom: 15px; font-size: 18px;">Recording Ready</h3>
        <video 
          controls 
          playsinline 
          style="max-width: 100%; max-height: 50vh; border-radius: 8px; margin-bottom: 15px;"
          src="${url}"
        ></video>
        <p style="margin-bottom: 15px; font-size: 14px; color: #ccc;">
          Long-press the video and tap "Save to Photos" or "Download"
        </p>
        <button id="recording-modal-close" style="
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
        ">Close</button>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    const closeBtn = document.getElementById('recording-modal-close');
    if (closeBtn) {
      closeBtn.onclick = () => {
        document.body.removeChild(modal);
        URL.revokeObjectURL(url);
      };
    }
    
    modal.onclick = (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
        URL.revokeObjectURL(url);
      }
    };
    
  }

  /**
   * Updates remote streams during recording
   * @param remoteStreams - Updated remote streams
   */
  updateRemoteStreams(remoteStreams: Map<string, { audio?: MediaStream; video?: MediaStream }>): void {
    if (!this.isRecording) return;

    // Remove videos for peers that left
    this.videoElements = this.videoElements.filter((item) => {
      if (item.isLocal || item.kind === 'screen') return true;
      return item.peerId && remoteStreams.has(item.peerId);
    });

    // Add new remote videos
    remoteStreams.forEach((streams, peerId) => {
      const isScreenSharePeer = this.remoteScreenShare?.peerId === peerId;
      const exists = this.videoElements.some((item) => item.peerId === peerId && item.kind === 'camera');
      if (!exists && streams.video && !isScreenSharePeer) {
        const remoteVideo = document.createElement('video');
        remoteVideo.srcObject = streams.video;
        remoteVideo.muted = true;
        remoteVideo.autoplay = true;
        remoteVideo.playsInline = true;
        remoteVideo.play().catch(console.error);
        this.videoElements.push({ video: remoteVideo, peerId, isLocal: false, kind: 'camera' });

        // Add audio if available
        if (streams.audio && this.audioContext && this.audioDestination) {
          const remoteSource = this.audioContext.createMediaStreamSource(streams.audio);
          remoteSource.connect(this.audioDestination);
          this.audioSources.push(remoteSource);
        }
      }
    });
  }

  /**
   * Updates the current recording layout (selected video, screen share state,,,)
   */
  updateLayout(options: {
    selectedVideoStream?: MediaStream | null;
    isScreenSharing?: boolean;
    localScreenStream?: MediaStream | null;
    remoteScreenShare?: { peerId: string; stream: MediaStream } | null;
  }): void {
    if (typeof options.selectedVideoStream !== 'undefined') {
      this.selectedVideoStream = options.selectedVideoStream;
    }
    if (typeof options.isScreenSharing !== 'undefined') {
      this.isScreenSharing = options.isScreenSharing;
    }
    if (typeof options.localScreenStream !== 'undefined') {
      this.localScreenStream = options.localScreenStream;
    }
    if (typeof options.remoteScreenShare !== 'undefined') {
      this.remoteScreenShare = options.remoteScreenShare;
    }

    this._updateScreenShareElements();
  }

  private _updateScreenShareElements(): void {
    this.videoElements = this.videoElements.filter((item) => item.kind !== 'screen');

    if (this.remoteScreenShare?.peerId) {
      this.videoElements = this.videoElements.filter((item) => {
        return !(item.kind === 'camera' && item.peerId === this.remoteScreenShare?.peerId);
      });
    }

    if (this.isScreenSharing && this.localScreenStream) {
      const screenVideo = document.createElement('video');
      screenVideo.srcObject = this.localScreenStream;
      screenVideo.muted = true;
      screenVideo.autoplay = true;
      screenVideo.playsInline = true;
      screenVideo.play().catch(console.error);
      this.videoElements.push({ video: screenVideo, isLocal: true, kind: 'screen' });
      return;
    }

    if (this.remoteScreenShare && this.remoteScreenShare.stream) {
      const screenVideo = document.createElement('video');
      screenVideo.srcObject = this.remoteScreenShare.stream;
      screenVideo.muted = true;
      screenVideo.autoplay = true;
      screenVideo.playsInline = true;
      screenVideo.play().catch(console.error);
      this.videoElements.push({
        video: screenVideo,
        peerId: this.remoteScreenShare.peerId,
        isLocal: false,
        kind: 'screen'
      });
    }
  }

  private _findVideoElementByStream(stream: MediaStream | null): VideoElement | undefined {
    if (!stream) return undefined;
    return this.videoElements.find((item) => item.video.srcObject === stream);
  }

  private _getMainVideoElement(): VideoElement | undefined {
    if (this.isScreenSharing && this.localScreenStream) {
      return this.videoElements.find((item) => item.kind === 'screen' && item.isLocal);
    }
    if (this.remoteScreenShare) {
      return this.videoElements.find((item) => item.kind === 'screen' && item.peerId === this.remoteScreenShare?.peerId);
    }

    if (this.selectedVideoStream) {
      const selected = this._findVideoElementByStream(this.selectedVideoStream);
      if (selected) return selected;
    }

    const localCam = this.videoElements.find((item) => item.isLocal && item.kind === 'camera');
    if (localCam) return localCam;

    return this.videoElements[0];
  }

  /**
   * Starts the canvas drawing loop for video compositing
   */
  private _startCanvasDrawing(): void {
    const draw = () => {
      if (!this.canvas || !this.canvasCtx) return;

      const width = this.canvas.width;
      const height = this.canvas.height;

      // Clear canvas with dark background
      this.canvasCtx.fillStyle = '#1a1a2e';
      this.canvasCtx.fillRect(0, 0, width, height);

      const screenElement = this._getScreenVideoElement();
      const cameraElements = this._getCameraVideoElements();

      if (screenElement && screenElement.video.readyState >= 2) {
        this._drawVideoInRect(screenElement.video, 0, 0, width, height, 'contain');
        const thumbnails = cameraElements
          .sort((a, b) => (a.isLocal === b.isLocal ? 0 : a.isLocal ? -1 : 1));

        const thumbWidth = Math.round(width * 0.18);
        const thumbHeight = Math.round(thumbWidth * (9 / 16));
        const margin = 16;
        let y = height - margin - thumbHeight;
        const x = width - margin - thumbWidth;

        thumbnails.forEach((item) => {
          if (y < margin) return; 
          const video = item.video;
          if (video.readyState < 2) return;

          this._drawVideoInRect(video, x, y, thumbWidth, thumbHeight, 'cover');

          this.canvasCtx.fillStyle = 'rgba(0, 0, 0, 0.6)';
          this.canvasCtx.fillRect(x, y + thumbHeight - 26, thumbWidth, 24);
          this.canvasCtx.fillStyle = '#fff';
          this.canvasCtx.font = '14px Arial';
          const label = item.isLocal ? 'You' : `Peer ${(item.peerId || '').slice(0, 6)}`;
          this.canvasCtx.fillText(label, x + 8, y + thumbHeight - 8);

          y -= thumbHeight + margin;
        });
      } else {
        if (cameraElements.length === 1) {
          const item = cameraElements[0];
          if (item.video.readyState >= 2) {
            this._drawVideoInRect(item.video, 0, 0, width, height, 'cover');
          }
        } else if (cameraElements.length > 1) {
          this._drawCameraGrid();
        }
      }

      // Add recording indicator
      this.canvasCtx.fillStyle = '#e74c3c';
      this.canvasCtx.beginPath();
      this.canvasCtx.arc(30, 30, 10, 0, Math.PI * 2);
      this.canvasCtx.fill();
      this.canvasCtx.fillStyle = '#fff';
      this.canvasCtx.font = 'bold 16px Arial';
      this.canvasCtx.fillText('REC', 50, 36);

      this.animationFrame = requestAnimationFrame(draw);
    };

    draw();
  }

  private _getCameraVideoElements(): VideoElement[] {
    return this.videoElements.filter((item) => item.kind === 'camera');
  }

  private _getScreenVideoElement(): VideoElement | undefined {
    return this.videoElements.find((item) => item.kind === 'screen');
  }

  private _drawCameraGrid(): void {
    if (!this.canvas || !this.canvasCtx) return;

    const cameras = this._getCameraVideoElements();
    const width = this.canvas.width;
    const height = this.canvas.height;

    const videoCount = cameras.length;
    const cols = Math.ceil(Math.sqrt(videoCount));
    const rows = Math.ceil(videoCount / cols);
    const cellWidth = width / cols;
    const cellHeight = height / rows;

    cameras.forEach((item, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = col * cellWidth;
      const y = row * cellHeight;

      const video = item.video;
      if (video.readyState < 2) return;

      this._drawVideoInRect(video, x, y, cellWidth, cellHeight, 'contain');

      this.canvasCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.canvasCtx.fillRect(x, y + cellHeight - 30, 100, 25);
      this.canvasCtx.fillStyle = '#fff';
      this.canvasCtx.font = '14px Arial';
      this.canvasCtx.fillText(item.isLocal ? 'You' : `Peer ${(item.peerId || '').slice(0, 6)}`, x + 5, y + cellHeight - 10);
    });
  }

  private _drawVideoInRect(
    video: HTMLVideoElement,
    x: number,
    y: number,
    width: number,
    height: number,
    fit: 'contain' | 'cover'
  ): void {
    if (video.readyState < 2) return;

    const videoAspect = video.videoWidth / video.videoHeight;
    const rectAspect = width / height;

    let drawWidth = width;
    let drawHeight = height;
    let drawX = x;
    let drawY = y;

    if (fit === 'contain') {
      if (videoAspect > rectAspect) {
        drawWidth = width;
        drawHeight = width / videoAspect;
        drawY = y + (height - drawHeight) / 2;
      } else {
        drawHeight = height;
        drawWidth = height * videoAspect;
        drawX = x + (width - drawWidth) / 2;
      }
    } else {
      // cover
      if (videoAspect > rectAspect) {
        drawHeight = height;
        drawWidth = height * videoAspect;
        drawX = x - (drawWidth - width) / 2;
      } else {
        drawWidth = width;
        drawHeight = width / videoAspect;
        drawY = y - (drawHeight - height) / 2;
      }
    }

    this.canvasCtx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
  }

  private _drawGrid(): void {
    if (!this.canvas || !this.canvasCtx) return;

    const width = this.canvas.width;
    const height = this.canvas.height;
    const videoCount = this.videoElements.length;

    const cols = Math.ceil(Math.sqrt(videoCount));
    const rows = Math.ceil(videoCount / cols);
    const cellWidth = width / cols;
    const cellHeight = height / rows;

    this.videoElements.forEach((item, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = col * cellWidth;
      const y = row * cellHeight;

      const video = item.video;
      if (video.readyState < 2) return;

      this._drawVideoInRect(video, x, y, cellWidth, cellHeight, 'contain');
      this.canvasCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.canvasCtx.fillRect(x, y + cellHeight - 30, 100, 25);
      this.canvasCtx.fillStyle = '#fff';
      this.canvasCtx.font = '14px Arial';
      this.canvasCtx.fillText(item.isLocal ? 'You' : `Peer ${(item.peerId || '').slice(0, 6)}`, x + 5, y + cellHeight - 10);
    });
  }

  /**
   * Stops the canvas drawing loop
   */
  private _stopCanvasDrawing(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Cleans up resources
   */
  private _cleanup(): void {
    this.videoElements.forEach((item) => {
      if (item.video.srcObject) {
        item.video.srcObject = null;
      }
    });
    this.videoElements = [];

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(console.error);
    }
    this.audioContext = null;
    this.audioDestination = null;
    this.audioSources = [];

    this.canvas = null;
    this.canvasCtx = null;
  }

  /**
   * Gets a supported MIME type for recording
   */
  private _getSupportedMimeType(): string {
    // Prefer mp4/h264 when supported , fallback to webm.
    const types = [
      'video/mp4;codecs=h264',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm';
  }

  private _getExtensionFromMimeType(mimeType: string): string {
    if (!mimeType) return 'webm';
    const normalized = mimeType.toLowerCase();
    if (normalized.includes('mp4')) return 'mp4';
    if (normalized.includes('webm')) return 'webm';
    if (normalized.includes('ogg')) return 'ogg';
    return 'webm';
  }

  /**
   * Checks if recording is supported
   */
  static isSupported(): boolean {
    return !!(window.MediaRecorder && window.AudioContext);
  }

  /**
   * Returns whether currently recording
   */
  getIsRecording(): boolean {
    return this.isRecording;
  }
}

export default RecordingService;
